import { create } from "zustand";
import { api, type GitDocEntry, type GitStatus } from "../api/client";

const CACHE_MS = 30_000;

interface GitState {
  docs: GitDocEntry[];
  status: GitStatus | null;
  loading: boolean;
  lastFetch: number;
  fetchTree: (force?: boolean) => Promise<GitDocEntry[]>;
  fetchStatus: () => Promise<GitStatus | null>;
  invalidate: () => void;
}

export const useGitStore = create<GitState>((set, get) => ({
  docs: [],
  status: null,
  loading: false,
  lastFetch: 0,

  fetchTree: async (force = false) => {
    const { lastFetch, docs } = get();
    if (!force && docs.length > 0 && Date.now() - lastFetch < CACHE_MS) {
      return docs;
    }
    set({ loading: true });
    try {
      const { docs: next } = await api.git.tree();
      set({ docs: next, lastFetch: Date.now(), loading: false });
      return next;
    } catch {
      set({ loading: false });
      return get().docs;
    }
  },

  fetchStatus: async () => {
    try {
      const status = await api.git.status();
      set({ status });
      return status;
    } catch {
      return null;
    }
  },

  invalidate: () => set({ lastFetch: 0 }),
}));
